import { usePasswordExpirationStatusQuery } from '@innovamat/glow-api-client';
import { useLocalStorage } from '@innovamat/hooks';
import { storage } from '@innovamat/radiance-utils';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';
import { useUser } from '../providers';

type PasswordExpirationOverlayState = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

const usePasswordExpirationOverlayStore =
  create<PasswordExpirationOverlayState>()((set) => ({
    isOpen: true,
    setIsOpen: (isOpen: boolean) => set({ isOpen }),
  }));

export enum Status {
  EXPIRED = 'EXPIRED',
  EXPIRES_SOON = 'EXPIRES_SOON',
  NOT_EXPIRED = 'NOT_EXPIRED',
}

type PasswordExpirationStatus = {
  expirationDate?: string | null;
  status: Status;
};

type UsePasswordExpirationStatus = {
  expirationDate: string | null;
  status: Status;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

function usePasswordExpirationStatus(): UsePasswordExpirationStatus {
  const { user, isRole } = useUser();

  const userExists = !!user;
  const isEnabled = !!(
    isRole.Teacher ||
    isRole.Admin ||
    isRole.DistrictAdmin ||
    isRole.Owner
  );
  const userLocale = user?.locale;

  const { isOpen, setIsOpen } = usePasswordExpirationOverlayStore();

  const [localStatus, setLocalStatus] =
    useLocalStorage<PasswordExpirationStatus | null>(
      storage.StorageKeys.PasswordExpirationStatus,
      null
    );

  const { i18n } = useTranslation();

  const shouldFetch = !localStatus;

  const { data, isLoading, isError, refetch } =
    usePasswordExpirationStatusQuery<{
      passwordExpirationStatus: PasswordExpirationStatus;
    }>(undefined, {
      enabled: shouldFetch && isEnabled,
      retry: false,
    });

  useEffect(() => {
    if (!userExists) {
      setIsOpen(true);
    }
  }, [userExists, setIsOpen]);

  useEffect(() => {
    if (data?.passwordExpirationStatus) {
      setLocalStatus(data.passwordExpirationStatus);
    }
  }, [data, setLocalStatus]);

  // workaround to avoid display the modal with default language "es"
  useEffect(() => {
    if (userLocale) {
      i18n.changeLanguage(userLocale);
    }
  }, [userLocale, i18n]);

  const handleRefetch = useCallback(async () => {
    const result = await refetch();

    if (result.data?.passwordExpirationStatus) {
      setLocalStatus(result.data.passwordExpirationStatus);
    }

    return result;
  }, [refetch, setLocalStatus]);

  const currentStatus = data?.passwordExpirationStatus || localStatus;

  const status = userExists
    ? currentStatus?.status ?? Status.NOT_EXPIRED
    : Status.NOT_EXPIRED;

  return {
    expirationDate: currentStatus?.expirationDate ?? null,
    status,
    isLoading: shouldFetch ? isLoading : false,
    isError: shouldFetch ? isError : false,
    refetch: handleRefetch,
    isOpen,
    setIsOpen,
  };
}

export { usePasswordExpirationStatus };
